// frontend/src/components/dfs/ContestResultHero.tsx
//
// Peak-end "finish strong" summary shown at the top of a FINALIZED contest for
// a user who actually entered. The daily loop's emotional climax is the result;
// this surfaces it (rank / score / percentile) instead of making the user hunt
// the leaderboard table for their own row. Pure daily content - safe on every
// platform including iOS (unlike the web-only LeagueCtaBanner).

import React from 'react';
import { Trophy } from 'lucide-react';

interface Props {
  rank: number;
  total: number;
  score: number;
}

/** 1 -> "1st", 2 -> "2nd", 47 -> "47th". */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const ContestResultHero: React.FC<Props> = ({ rank, total, score }) => {
  const isWin = rank === 1;

  return (
    <div className="bg-gradient-to-r from-surface-card to-surface-raised border border-accent-yellow/40 rounded-lg shadow-xl p-4 md:p-5">
      <div className="flex items-center gap-4">
        <Trophy
          size={32}
          className={`flex-shrink-0 ${isWin ? 'text-accent-yellow' : 'text-accent-light-blue'}`}
        />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-400">Final Result</p>
          <p className="font-heading font-bold text-xl md:text-2xl text-white leading-tight">
            {isWin ? 'You won! ' : 'You finished '}
            <span className="text-accent-yellow">{ordinal(rank)}</span>
            <span className="text-gray-400 font-normal text-base"> of {total.toLocaleString()}</span>
          </p>
          <p className="text-sm font-bold text-accent-yellow mt-1">{score.toFixed(1)} pts</p>
        </div>
      </div>
    </div>
  );
};

export default ContestResultHero;
